import React from "react";
import { motion } from "framer-motion";
import { Server, Lock, Eye, ClipboardCheck, FileText, AlertTriangle, ShieldCheck } from "lucide-react";
import PageLayout from "@/components/PageLayout";

const PRACTICES = [
  { icon: Server, title: "Infrastructure Security", text: "QNNEAL operates on enterprise-grade cloud infrastructure with multi-region redundancy. All data is encrypted in transit using TLS 1.3 and at rest using AES-256 encryption. Our infrastructure is monitored 24/7 with automated threat detection and incident response." },
  { icon: Lock, title: "Authentication & MFA", text: "User authentication is secured with bcrypt password hashing (12 rounds), JWT token rotation (15-minute access, 7-day refresh), and account lockout after 5 failed login attempts. Multi-factor authentication (TOTP) is supported via Google Authenticator, Authy, and 1Password, with 10 single-use backup codes per user." },
  { icon: Eye, title: "Role-Based Access Control (RBAC)", text: "Four granular roles enforce the principle of least privilege: Owner (full control, billing, team management), Admin (manage users, keys, vault), Developer (create keys, use gateway), and Viewer (read-only). Per-user data isolation ensures no user can access another's credentials, configurations, or usage data." },
  { icon: ClipboardCheck, title: "SOC 2 Compliance Controls", text: "Our platform implements SOC 2 CC6.1 (logical access), CC6.7 (session management), and CC7.2 (system monitoring) controls. All authentication, API key creation, role changes, and configuration modifications are tracked in an immutable audit trail. Session management allows users to view and revoke active sessions with device and IP tracking." },
  { icon: FileText, title: "Comprehensive Audit Logging", text: "Every API call, login, key creation, role change, team invite, MFA event, and configuration change is logged with timestamp, IP address, and user agent. Audit logs are retained for 90 days and available through the Settings dashboard. Enterprise customers can export logs for compliance reporting." },
  { icon: AlertTriangle, title: "API Key Security", text: "API keys use the qng_sk_ prefix format and are stored exclusively as SHA-256 hashes. Plaintext keys are shown exactly once at creation. Keys support granular permissions, revocation, and regeneration. Rate limiting protects auth endpoints: 5 logins/min, 3 registrations/min." },
];

export default function SecurityPage() {
  return (
    <PageLayout>
      <div className="max-w-5xl mx-auto px-6 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="security-heading">Enterprise-Grade Security by Design</h1>
          <p className="text-lg text-[var(--qn-foreground-muted)] max-w-3xl mb-16 leading-relaxed">
            Security is not an afterthought at QNNEAL &mdash; it is foundational to everything we build. Our platform is designed to meet the stringent requirements of enterprises, research institutions, and government agencies.
          </p>
        </motion.div>

        <div className="space-y-8">
          {PRACTICES.map((p, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08 * i }}
              className="flex gap-5 p-6 rounded-xl border border-[var(--qn-border)] bg-[var(--qn-surface)]"
              data-testid={`security-practice-${i}`}
            >
              <p.icon className="w-8 h-8 text-[var(--qn-primary)] shrink-0 mt-0.5" />
              <div>
                <h2 className="font-semibold text-base mb-2">{p.title}</h2>
                <p className="text-sm text-[var(--qn-foreground-muted)] leading-relaxed">{p.text}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <section className="mt-12 p-6 rounded-xl border border-[var(--qn-primary)]/20 bg-[var(--qn-primary-muted)]">
          <div className="flex items-center gap-3 mb-3">
            <ShieldCheck className="w-6 h-6 text-[var(--qn-primary)]" />
            <h3 className="font-semibold">Vulnerability Disclosure</h3>
          </div>
          <p className="text-sm text-[var(--qn-foreground-muted)] leading-relaxed">
            We welcome responsible disclosure of security vulnerabilities. If you discover a potential security issue, please report it to{" "}
            <a href="mailto:security@qnneal.dev" className="text-[var(--qn-primary)] hover:underline">security@qnneal.dev</a>.
            We commit to acknowledging reports within 24 hours and providing status updates throughout the resolution process.
          </p>
        </section>
      </div>
    </PageLayout>
  );
}
